<template>
    <div>
    <!-- Background Section -->
    <div class="relative bg-no-repeat bg-center bg-cover grow flex flex-col-reverse pb-6 lg:pb-10 h-[15rem]"
      :style="{
        backgroundImage: `url(${video.cover})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }">
      <div class="absolute inset-0 bg-[#2C5BA1] bg-opacity-40 rounded-2xl"></div>
      <div class="absolute inset-0 flex flex-col items-center justify-center text-white text-3xl font-bold">
        <h1>{{ $t('explorVideo') }} {{ getLocalizedField('name') }}</h1>
        <div class="text-xs lg:text-base font-semibold text-white mt-4">
          <p class="flex flex-row gap-2">
            <a aria-label="Lien Page accueil" class="hover:underline" :href="`/videos`">{{ $t("video") }}</a>
            <span>/</span>
            <span class="hover:underline">{{ getLocalizedField('name') }}</span>
          </p>
        </div>
      </div>
    </div>
      <div class="min-h-screen bg-white">
        <!-- Header/Navigation -->
        <div class="max-w-7xl mx-auto px-4 py-6">
          <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <!-- Main Video Section -->
            <div class="lg:col-span-2 space-y-4">
              <!-- Video Player -->
              <div
                class="relative w-full aspect-video bg-black rounded-lg overflow-hidden"
              >
                <div v-if="!video">{{ $t('loading') }}</div>
                <video
                  v-else
                  :key="video.slug"
                  class="w-full h-full"
                  controls
                  :poster="videoPoster"
                  ref="videoPlayer"
                  @loadedmetadata="onVideoLoaded"
                  @timeupdate="onTimeUpdate"
                  @ended="onVideoEnded"
                >
                  <source :src="video.videoLink" type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
    
                <!-- Custom Video Overlay (optional) -->
                <div
                  v-if="showVideoOverlay"
                  class="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center"
                >
                  <button
                    @click="playVideo"
                    class="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center hover:bg-red-700 transition-colors"
                  >
                    <i class="fas fa-play text-white text-2xl ml-1"></i>
                  </button>
                </div>
              </div>
    
              <!-- Video Progress Bar -->
              <div class="w-full bg-gray-200 rounded-full h-1">
                <div
                  class="bg-red-600 h-1 rounded-full transition-all duration-300"
                  :style="{ width: videoProgress + '%' }"
                ></div>
              </div>
    
              <!-- Video Title -->
              <div class="space-y-3">
                <h1 class="text-xl font-semibold text-gray-900 leading-tight">
                  <template v-if="currentLanguage === 'fr'">{{ video.name }}</template>
                  <template v-else-if="currentLanguage === 'ar'">{{ video.name_ar }}</template>
                  <template v-else-if="currentLanguage === 'en'">{{ video.name_en }}</template>
                  <template v-else-if="currentLanguage === 'ru'">{{ video.name_ru }}</template>
                  <template v-else-if="currentLanguage === 'ja'">{{ video.name_ja }}</template>
                  <template v-else-if="currentLanguage === 'ko'">{{ video.name_ko }}</template>
                  <template v-else-if="currentLanguage === 'zh'">{{ video.name_zh }}</template>
                  <template v-else>{{ video.name }}</template>
                </h1>
    
                <!-- Video Stats and Actions -->
                <div class="flex items-center justify-between flex-wrap gap-4">
                  <!-- <div class="text-sm text-gray-600">
                    {{ formatViews(currentVideo.viewCount) }} {{ $t('views') }} •
                    {{ currentVideo.uploadDate }}
                  </div> -->
                    <button
                      @click="downloadVideo"
                      class="flex items-center gap-2 px-3 font-semibold py-2 rounded-lg hover:bg-gray-100 transition-colors"
                    >
                      <i v-if="!isDownloading" class="fas fa-download text-blue"></i>
                      <i
                        v-if="isDownloading"
                        class="fas fa-spinner text-blue fa-spin ml-2"
                      ></i>
                      {{ $t('download') }}
                    </button>
                  <div class="flex items-center gap-2">
                    <div class="relative">
                      <button
                        @click="toggleSocialShare"
                        class="flex items-center gap-2 font-semibold px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors"
                      >
                        <i class="fas fa-share text-blue"></i>
                        {{ $t('share') }}
                      </button>
    
                      <!-- Social Media Share Dropdown -->
                      <div
                        v-if="showSocialShare"
                        class="absolute left-0 top-full mt-2 bg-white border border-gray-200 rounded-lg shadow-lg py-3 z-20 min-w-80"
                      >
                        <div class="px-4 pb-3 border-b border-gray-100">
                          <h3 class="font-semibold text-gray-900 mb-2">
                            {{ $t('shareThisVideo') }}
                          </h3>
    
                          <!-- Social Media Buttons -->
                          <div class="grid grid-cols-4 gap-3 mb-4">
                            <button
                              @click="shareOnFacebook"
                              class="flex flex-col items-center gap-2 p-3 rounded-lg hover:bg-blue-50 transition-colors group"
                            >
                              <div
                                class="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center group-hover:bg-blue-700 transition-colors"
                              >
                                <i class="fab fa-facebook-f text-white"></i>
                              </div>
                              <span class="text-xs text-gray-700">{{ $t('facebook') }}</span>
                            </button>
    
                            <button
                              @click="shareOnTwitter"
                              class="flex flex-col items-center gap-2 p-3 rounded-lg hover:bg-blue-50 transition-colors group"
                            >
                              <div
                                class="w-10 h-10 bg-black rounded-full flex items-center justify-center group-hover:bg-gray-800 transition-colors"
                              >
                                <i class="fab fa-twitter text-white"></i>
                              </div>
                              <span class="text-xs text-gray-700">{{ $t('twitter') }}</span>
                            </button>
    
                            <button
                              @click="shareOnInstagram"
                              class="flex flex-col items-center gap-2 p-3 rounded-lg hover:bg-pink-50 transition-colors group"
                            >
                              <div
                                class="w-10 h-10 bg-gradient-to-br from-purple-600 via-pink-600 to-orange-400 rounded-full flex items-center justify-center group-hover:from-purple-700 group-hover:via-pink-700 group-hover:to-orange-500 transition-colors"
                              >
                                <i class="fab fa-instagram text-white"></i>
                              </div>
                              <span class="text-xs text-gray-700">{{ $t('instagram') }}</span>
                            </button>
    
                            <button
                              @click="shareOnLinkedIn"
                              class="flex flex-col items-center gap-2 p-3 rounded-lg hover:bg-blue-50 transition-colors group"
                            >
                              <div
                                class="w-10 h-10 bg-blue-700 rounded-full flex items-center justify-center group-hover:bg-blue-800 transition-colors"
                              >
                                <i class="fab fa-linkedin-in text-white"></i>
                              </div>
                              <span class="text-xs text-gray-700">{{ $t('linkedin') }}</span>
                            </button>
    
                            <button
                              @click="shareOnWhatsApp"
                              class="flex flex-col items-center gap-2 p-3 rounded-lg hover:bg-green-50 transition-colors group"
                            >
                              <div
                                class="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center group-hover:bg-green-600 transition-colors"
                              >
                                <i class="fab fa-whatsapp text-white"></i>
                              </div>
                              <span class="text-xs text-gray-700">{{ $t('whatsapp') }}</span>
                            </button>
                            <button
                              @click="shareOnReddit"
                              class="flex flex-col items-center gap-2 p-3 rounded-lg hover:bg-orange-50 transition-colors group"
                            >
                              <div
                                class="w-10 h-10 bg-orange-600 rounded-full flex items-center justify-center group-hover:bg-orange-700 transition-colors"
                              >
                                <i class="fab fa-reddit-alien text-white"></i>
                              </div>
                              <span class="text-xs text-gray-700">{{ $t('reddit') }}</span>
                            </button>
    
                            <button
                              @click="copyVideoLink"
                              class="flex flex-col items-center gap-2 p-3 rounded-lg hover:bg-gray-50 transition-colors group"
                            >
                              <div
                                class="w-10 h-10 bg-gray-500 rounded-full flex items-center justify-center group-hover:bg-gray-600 transition-colors"
                              >
                                <i class="fas fa-copy text-white"></i>
                              </div>
                              <span class="text-xs text-gray-700">{{ $t('copyLink') }}</span>
                            </button>
                          </div>
                        </div>
    
                        <!-- Share URL Input -->
                        <div class="px-4 pt-3">
                          <div
                            class="flex items-center gap-2 bg-gray-50 rounded-lg p-2"
                          >
                            <input
                              :value="shareUrl"
                              readonly
                              class="flex-1 bg-transparent text-sm text-gray-700 outline-none"
                              placeholder="Video URL"
                            />
                            <button
                              @click="copyVideoLink"
                              class="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition-colors"
                            >
                              {{ $t('copyLink') }}
                            </button>
                          </div>
    
                          <!-- Embed Code Option -->
                          <div class="mt-3">
                            <button
                              @click="showEmbedCode = !showEmbedCode"
                              class="text-sm text-blue-600 hover:text-blue-700 transition-colors"
                            >
                              {{ showEmbedCode ? "Hide" : "Show" }} {{ $t('copyEmbedCode') }}
                            </button>
    
                            <div v-if="showEmbedCode" class="mt-2">
                              <textarea
                                :value="embedCode"
                                readonly
                                class="w-full h-20 text-xs bg-gray-50 border border-gray-200 rounded p-2 resize-none"
                                placeholder="Embed code"
                              ></textarea>
                              <button
                                @click="copyEmbedCode"
                                class="mt-2 px-3 py-1 bg-gray-600 text-white text-sm rounded hover:bg-gray-700 transition-colors"
                              >
                                {{ $t('copyEmbedCode') }}
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
    
                  </div>
                </div>
              </div>
    
              <div class="border-t border-gray-200"></div>
              <!-- Video Description -->
              <!-- <div class="bg-gray-50 rounded-lg p-4">
                <p
                  v-if="currentLanguage==='fr'"
                  class="text-sm text-gray-700 leading-relaxed whitespace-pre-line"
                  v-html="
                    showFullDescription
                      ? currentVideo.description
                      : truncatedDescription
                  "
                ></p>
                  <p
                  v-if="currentLanguage==='ar'"
                  class="text-sm text-gray-700 leading-relaxed whitespace-pre-line"
                  v-html="
                    showFullDescription
                      ? currentVideo.description_ar
                      : truncatedDescription
                  "
                ></p>
                  <p
                  v-if="currentLanguage==='ru'"
                  class="text-sm text-gray-700 leading-relaxed whitespace-pre-line"
                  v-html="
                    showFullDescription
                      ? currentVideo.description_ru
                      : truncatedDescription
                  "
                ></p>
                  <p
                  v-if="currentLanguage==='zh'"
                  class="text-sm text-gray-700 leading-relaxed whitespace-pre-line"
                  v-html="
                    showFullDescription
                      ? currentVideo.description_zh
                      : truncatedDescription
                  "
                ></p>
                  <p
                  v-if="currentLanguage==='ja'"
                  class="text-sm text-gray-700 leading-relaxed whitespace-pre-line"
                  v-html="
                    showFullDescription
                      ? currentVideo.description_ja
                      : truncatedDescription
                  "
                ></p>
                  <p
                  v-if="currentLanguage==='ko'"
                  class="text-sm text-gray-700 leading-relaxed whitespace-pre-line"
                  v-html="
                    showFullDescription
                      ? currentVideo.description_ko
                      : truncatedDescription
                  "
                ></p>
                <p
                  v-if="currentLanguage==='en'"
                  class="text-sm text-gray-700 leading-relaxed whitespace-pre-line"
                  v-html="
                    showFullDescription
                      ? currentVideo.description_en
                      : truncatedDescription
                  "
                ></p>
                <button
                  v-if="currentVideo.description.length > 400"
                  @click="showFullDescription = !showFullDescription"
                  class="mt-2 text-gray-600 text-sm hover:text-gray-800 transition-colors font-medium"
                >
                  {{ showFullDescription ? $t('showMore') : $t('showLess') }}
                </button>
              </div> -->
            </div>
    
            <!-- Related Videos Sidebar -->
            <div class="space-y-4">
              <div class="flex items-center justify-between">
                <h2 class="text-lg font-semibold text-gray-900">{{ $t('relatedVideos') }}</h2>
                <button
                  @click="shuffleRelatedVideos"
                  class="text-sm text-gray-600 hover:text-gray-800 transition-colors"
                >
                  <i class="fas fa-random"></i>
                </button>
              </div>
    
              <div class="space-y-3 h-96 overflow-y-auto" v-if="relatedVideos.length>0">
                <a
                  aria-label="related videos" 
                  v-for="video in relatedVideos"
                  :key="video.id"
                  :href="`#${video.slug}`"
                  @click.prevent="switchToVideo(video)"
                  class="block group"
                >
                  <div
                    class="flex gap-3 hover:bg-gray-50 rounded-lg p-2 transition-colors"
                  >
                    <div class="relative flex-shrink-0">
                    <img
                      :src="video.vignette"
                      :alt="video.name"
                      width="168"
                      height="96"
                      class="rounded-lg object-cover w-42 h-24"
                    />
                      <div
                        class="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-200 rounded-lg flex items-center justify-center"
                      >
                        <i
                          class="fas fa-play text-white opacity-0 group-hover:opacity-100 transition-opacity text-xl"
                        ></i>
                      </div>
                    </div>
                    <div class="flex-1 min-w-0">
                      <h3
                        class="text-sm font-medium text-gray-900 line-clamp-2 group-hover:text-blue-600 transition-colors"
                      >
                        <template v-if="currentLanguage === 'fr'">{{ video.name }}</template>
                        <template v-else-if="currentLanguage === 'ar'">{{ video.name_ar }}</template>
                        <template v-else-if="currentLanguage === 'en'">{{ video.name_en }}</template>
                        <template v-else-if="currentLanguage === 'ru'">{{ video.name_ru }}</template>
                        <template v-else-if="currentLanguage === 'ja'">{{ video.name_ja }}</template>
                        <template v-else-if="currentLanguage === 'ko'">{{ video.name_ko }}</template>
                        <template v-else-if="currentLanguage === 'zh'">{{ video.name_zh }}</template>
                      </h3>
                    </div>
                  </div>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
<div class="container p-3.5 sm:px-6 md:px-8 lg:px-16 xl:px-16 mx-auto my-2">
        <div class="container p-3.5 sm:px-6 md:px-8 lg:px-16 xl:px-16 mx-auto my-2">
                        <h1 class="text-xl text-center font-bold mb-4">
          {{ $t("latest_articles") }}
        </h1>
        <div
          class="container p-3.5 sm:px-4 md:px-8 lg:px-16 xl:px-16 mx-auto my-2"
        >
          <ArticleList :articles="articles" />
        </div>
        <div
    class="container p-3.5 lg:px-12 min-1700:pl-6 min-1700:pr-12 mx-auto my-2 relative"
    v-if="this.imageListBanner1.length !== 0"
    data-aos="fade-up"
  >
    <BannerPub :images="this.imageListBanner1" />
  </div>    
        </div>
    </div>
    </div>
</template>
<script>
import videos from "@/assets/video.json";
import BannerPub from "../partial/BannerPub.vue";
import ArticleList from "../partial/ArticleList.vue";
import { mapActions } from "vuex";
import { updateMetaTagsPage } from "../utils/filters";
export default {
    components: {
        BannerPub,
        ArticleList
    }, 
    computed:{
        embedCode() {
            return `<iframe width="560" height="315" src="${this.shareUrl}" frameborder="0" allowfullscreen></iframe>`;
        },
    },
      watch: {
    "$i18n.locale"(newLocale) {
      this.currentLanguage = newLocale;
      this.updateMetaTags(this.video,this.currentLanguage);
      this.addCanonicalLink();
    },
  },
    data(){
        return{
            currentLanguage: this.$i18n.locale,                    
            imageListBanner1: [],
            imageListBanner2: [],
            imageListBanner3: [],
            articles: [],
            video:null,
            videos,
            autoplay: true,
            showVideoOverlay: false,
            videoProgress: 0,
            showMoreOptions: false,
            showSocialShare: false,
            shareUrl: "",
            shareTitle: "",
            shareDescription: "",
            showEmbedCode: false,
            videoPoster: "",
            relatedVideos: videos,
        }
    },
    methods:{
        ...mapActions(["fetchBanners", "fetchArticles"]),
            addCanonicalLink() {
        const currentUrl = window.location.href; // Get the current URL
        const currentLanguage = this.currentLanguage; // Fetch the current language
        let canonicalLink = document.querySelector("link[rel='canonical']");
  
        // Modify the URL based on the language
        const url = new URL(currentUrl);
        const pathSegments = url.pathname.split("/").filter(Boolean); // Remove empty segments
  
        if (currentLanguage === "ar" || currentLanguage === "en" || currentLanguage === "kor" || currentLanguage === "ru" || currentLanguage === 'zh' || currentLanguage === 'ja') {
          // Add language prefix if not already present
          if (pathSegments[0] !== currentLanguage) {
            pathSegments.unshift(currentLanguage);
          }
        } else if (
          (currentLanguage === "fr" && pathSegments[0] === "ar") ||
          pathSegments[0] === "en"||
          pathSegments[0] === "ru" || pathSegments[0] === 'ja' || pathSegments[0] === 'zh'  || currentLanguage === "kor"
        ) {
          // Remove language prefix if "fr" (default)
          pathSegments.shift();
        }
  
        url.pathname = "/" + pathSegments.join("/"); // Construct the new path
        const canonicalUrl = url.toString(); // Get the full URL as a string
  
        if (canonicalLink) {
          // Update the existing canonical link
          canonicalLink.setAttribute("href", canonicalUrl);
        } else {
          // Create a new canonical link
          canonicalLink = document.createElement("link");
          canonicalLink.setAttribute("rel", "canonical");
          canonicalLink.setAttribute("href", canonicalUrl);
          document.head.appendChild(canonicalLink);
        }
        },
                updateMetaTags(video,language) {
const metaData = {
  // Russian (ru)
  "meta_title_ru": video.seo.meta_title_ru+" - "+video.name_ru,
"meta_description_ru": video.seo.meta_description_ru+" - "+video.name_ru,
  "meta_keywords_ru": video.seo.meta_keywords_ru+" - "+video.name_ru,

  // French (fr)
 "meta_title_fr": video.seo.meta_title+" - "+video.name,
  "meta_description_fr": video.seo.meta_description+" - "+video.name,
  "meta_keywords_fr": video.seo.meta_keywords+" - "+video.name,

  // English (en)
 "meta_title_en": video.seo.meta_title_en+" - "+video.name_en,
   "meta_description_en": video.seo.meta_description_en+" - "+video.name_en,
    "meta_keywords_en": video.seo.meta_keywords_en+" - "+video.name_en,

  // Arabic (ar)
   "meta_title_ar": video.seo.meta_title_ar+" - "+video.name_ar,
 "meta_description_ar": video.seo.meta_description_ar+" - "+video.name_ar,
 "meta_keywords_ar": video.seo.meta_keywords_ar+" - "+video.name_ar,

  // Chinese (chi)
  "meta_title_zh": video.seo.meta_title_zh+" - "+video.name_zh,
  "meta_description_zh": video.seo.meta_description_zh+" - "+video.name_zh,
  "meta_keywords_zh": video.seo.meta_keywords_zh+" - "+video.name_zh,

  // Korean (kor)
  "meta_title_ko": video.seo.meta_title_ko+" - "+video.name_ko,
  "meta_description_ko": video.seo.meta_description_ko+" - "+video.name_ko,
  "meta_keywords_ko": video.seo.meta_keywords_ko+" - "+video.name_ko,

  // Japanese (jpn)
  "meta_title_ja": video.seo.meta_title_ja+" - "+video.name_ja,
  "meta_description_ja": video.seo.meta_description_ja+" - "+video.name_ja,
  "meta_keywords_ja": video.seo.meta_keywords_ja+" - "+video.name_ja,
};
updateMetaTagsPage(language,metaData,'Videos')
    },
        loadVideo(){
            this.video=videos.filter(x=>x.slug===this.$route.params.slug)[0]
            this.videoPoster=this.video.vignette
            this.updateMetaTags(this.video,this.currentLanguage)
        },
              async loadBannerData() {
        try {
          const data = await this.fetchBanners();
          let arrayBanners = Object.values(data);
          this.imageListBanner1 = arrayBanners[0];
          this.imageListBanner2 = arrayBanners[1];
          this.imageListBanner3 = arrayBanners[2];
        } catch (error) {
          console.error("Error loading banners: ", error);
        }
      },
      async loadArticles() {
        try {
          const articlesList = await this.fetchArticles();
          this.articles = articlesList;
        } catch (error) {
          console.error("Error loading articlesList: ", error);
        }
      },
        getLocalizedField(field) {
            const localizedField = `${field}_${this.currentLanguage === 'fr' ? '' : `${this.currentLanguage}`}`;
            return this.video[localizedField] || this.video[field] || '';
        },
        toggleSocialShare() {
            this.showSocialShare = !this.showSocialShare;
            if (this.showSocialShare) {
                this.updateShareData();
            }
        },
        updateShareData() {
            this.shareUrl = window.location.href;
            this.shareTitle = this.video.name;
            this.shareDescription =
            this.video.description.substring(0, 200) + "...";
        },
        shareOnFacebook() {
            const url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(this.shareUrl)}&quote=${encodeURIComponent(this.shareTitle)}`;
            this.openShareWindow(url, "Facebook");
        },
        shareOnTwitter() {
            const text = `${this.shareTitle} - ${this.shareDescription}`;
            const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(this.shareUrl)}&hashtags=vue,javascript,tutorial`;
            this.openShareWindow(url, "Twitter");
        },
            shareOnInstagram() {
      this.copyVideoLink();
      alert(
        "Link copied! Open Instagram and paste the link in your story or post."
      );
    },
    shareOnLinkedIn() {
      const url = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(this.shareUrl)}&title=${encodeURIComponent(this.shareTitle)}&summary=${encodeURIComponent(this.shareDescription)}`;
      this.openShareWindow(url, "LinkedIn");
    },
    shareOnWhatsApp() {
      const text = `${this.shareTitle}\n${this.shareUrl}`;
      const url = `https://wa.me/?text=${encodeURIComponent(text)}`;
      this.openShareWindow(url, "WhatsApp");
    },
    shareViaEmail() {
      const subject = encodeURIComponent(
        `Check out this video: ${this.shareTitle}`
      );
      const body = encodeURIComponent(
        `I thought you might be interested in this video:\n\n${this.shareTitle}\n\n${this.shareDescription}\n\nWatch it here: ${this.shareUrl}`
      );
      const url = `mailto:?subject=${subject}&body=${body}`;
      window.location.href = url;
    },
    shareOnReddit() {
      const url = `https://reddit.com/submit?url=${encodeURIComponent(this.shareUrl)}&title=${encodeURIComponent(this.shareTitle)}`;
      this.openShareWindow(url, "Reddit");
    },
    async copyVideoLink() {
      try {
        await navigator.clipboard.writeText(this.shareUrl);
        this.showCopySuccess();
      } catch (err) {
        // Fallback for older browsers
        const textArea = document.createElement("textarea");
        textArea.value = this.shareUrl;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand("copy");
        document.body.removeChild(textArea);
        this.showCopySuccess();
      }
    },
    async copyEmbedCode() {
      try {
        await navigator.clipboard.writeText(this.embedCode);
        this.showCopySuccess("Embed code copied!");
      } catch (err) {
        // Fallback for older browsers
        const textArea = document.createElement("textarea");
        textArea.value = this.embedCode;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand("copy");
        document.body.removeChild(textArea);
        this.showCopySuccess("Embed code copied!");
      }
    },
    openShareWindow(url, platform) {
      const width = 600;
      const height = 400;
      const left = (window.innerWidth - width) / 2;
      const top = (window.innerHeight - height) / 2;

      window.open(
        url,
        `share-${platform}`,
        `width=${width},height=${height},left=${left},top=${top},scrollbars=yes,resizable=yes`
      );

      this.showSocialShare = false;
    },
    showCopySuccess(message = "Link copied to clipboard!") {
      // Create a temporary notification
      const notification = document.createElement("div");
      notification.textContent = message;
      notification.className =
        "fixed top-4 right-4 bg-green-600 text-white px-4 py-2 rounded-lg shadow-lg z-50 transition-all duration-300";
      document.body.appendChild(notification);

      setTimeout(() => {
        notification.style.opacity = "0";
        setTimeout(() => {
          document.body.removeChild(notification);
        }, 300);
      }, 2000);

      this.showSocialShare = false;
    },
    formatViews(count) {
      if (count >= 1000000) {
        return (count / 1000000).toFixed(1) + "M";
      } else if (count >= 1000) {
        return (count / 1000).toFixed(1) + "K";
      }
      return count.toString();
    },
    toggleAutoplay() {
      this.autoplay = !this.autoplay;
    },
    autoResizeTextarea(event) {
      const textarea = event.target;
      textarea.style.height = "auto";
      textarea.style.height = textarea.scrollHeight + "px";
    },
    switchToVideo(video) {
      this.video=video
      this.videoPoster = video.vignette;
      this.videoProgress = 0;
      this.showVideoOverlay = true;
      // Scroll to top
      window.scrollTo({ top: 0, behavior: "smooth" });
    },
    shuffleRelatedVideos() {
      const shuffled = [...this.relatedVideos];
      for (let i = shuffled.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
      }
      this.relatedVideos = shuffled;
    },
    async downloadVideo() {
      try {
        this.isDownloading = true;
        // Check if we have a valid video URL
        if (!this.video.videoLink) {
          throw new Error("No video available to download");
        }

        // Create a temporary anchor element
        const link = document.createElement("a");

        // Set the download attribute with a filename
        const urlParts = this.video.videoLink.split("/");
        const filename =
          urlParts[urlParts.length - 1].split("?")[0] || "hotel-video.mp4";
        link.download = filename;

        // Set the href to the video URL
        link.href = this.video.videoLink;

        // Required for Firefox
        document.body.appendChild(link);

        // Trigger the download
        link.click();

        // Clean up
        document.body.removeChild(link);

        // Show success notification
        this.showCopySuccess("Download started!");
      } catch (error) {
        console.error("Download error:", error);
        this.showCopySuccess("Download failed: " + error.message);
        this.isDownloading = false;
      } finally {
        this.isDownloading = false;
      }
    },
    playVideo() {
      if (this.$refs.videoPlayer) {
        this.$refs.videoPlayer.play();
        this.showVideoOverlay = false;
      }
    },
    onVideoLoaded() {
      console.log("Video loaded");
    },
    onTimeUpdate() {
      if (this.$refs.videoPlayer) {
        const progress =
          (this.$refs.videoPlayer.currentTime /
            this.$refs.videoPlayer.duration) *
          100;
        this.videoProgress = progress || 0;
      }
    },
    onVideoEnded() {
      if (this.autoplay) {
        // Get next index using modulo to loop back to start
        const nextIndex =
          (this.currentVideoIndex + 1) % this.relatedVideos.length;
        this.currentVideoIndex = nextIndex;

        let nextVideo = this.relatedVideos[nextIndex];

        if (nextVideo) {
          this.switchToVideo(nextVideo);

          // Play the new video immediately
          this.$nextTick(() => {
            if (this.$refs.videoPlayer) {
              this.$refs.videoPlayer.play().catch((error) => {
                console.log("Autoplay prevented:", error);
                this.showVideoOverlay = true;
              });
            }
          });
        }
      }
    },
    handleClickOutside(event) {
      if (!event.target.closest(".relative")) {
        this.showMoreOptions = false;
        this.showSocialShare = false;
      }
    },
    },
    async created() {
      this.$store.dispatch("setLoading", true);
      try {
        this.loadVideo();
        await Promise.all([this.loadBannerData(), this.loadArticles()]);
      } catch (error) {
        console.log("error ", error);
      } finally {
        this.$store.dispatch("setLoading", false);
      }
    },
}
</script>